import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, CalendarClock } from "lucide-react";
import { WEEKDAYS_FULL } from "@/lib/constants";
import { formatDateIT } from "@/lib/dates";
import { italianCollator, sortCompaniesIt } from "@/lib/sortCompanies";
import { reconcileEmployeeWithCompany } from "@/lib/companyEmployeeCoherence";
import ScheduleExceptionsCard from "@/components/ScheduleExceptionsCard";
import { announceShellCompany, companyNameForShell } from "@/lib/shellCompany";
import { SuiteEmployeePicker } from "@/components/suite";
import { useEffectiveRole } from "@/foundation/context";
import { fmtHours } from "@/foundation/formatting";
import { DurationInput, DurationLabel, DurationFormatBadge } from "@/foundation/duration";
import { WeeklyScheduleEditor } from "@/foundation/timesheet";

const days = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
const empty = () => ({
  name: "Standard",
  effective_from: new Date().toISOString().slice(0, 10),
  monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 0, sunday: 0,
  // Alternanza A/B
  alternation_active: false,
  alternation_starts_on_odd_week: true,
  monday_alt: 0, tuesday_alt: 0, wednesday_alt: 0, thursday_alt: 0, friday_alt: 0, saturday_alt: 0, sunday_alt: 0,
  // Quota sabati mensile
  saturday_quota_days_per_month: 0,
  saturday_hours_per_worked_day: 0,
});

export default function HourProfiles() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useCompanySelection();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());

  useEffect(() => {
    (async () => {
      try {
        if (["admin", "super_admin"].includes(effectiveRole)) {
          const { data } = await api.get("/companies");
          setCompanies(sortCompaniesIt(data));
        }
      } catch (e) { toast.error(apiErr(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (["admin", "super_admin"].includes(effectiveRole) && companyId) params.company_id = companyId;
        const { data } = await api.get("/employees", { params });
        const sortedEmployees = [...data].sort((a, b) => {
          const lastName = italianCollator.compare(a?.last_name || "", b?.last_name || "");
          if (lastName !== 0) return lastName;
          return italianCollator.compare(a?.first_name || "", b?.first_name || "");
        });
        setEmployees(sortedEmployees);
        // v0.99.30-c2 fix · Coerenza azienda/dipendente (helper centrale).
        setEmployeeId((prev) => reconcileEmployeeWithCompany(
          prev,
          sortedEmployees,
          { autoSelectFirst: true },
        ));
      } catch (e) { toast.error(apiErr(e)); }
    })();
    /* eslint-disable-next-line */
  }, [companyId]);

  const load = async () => {
    if (!employeeId) {
      setProfiles([]);
      return;
    }
    try {
      const { data } = await api.get("/hour-profiles", { params: { employee_id: employeeId } });
      setProfiles(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);
  useEffect(() => {
    announceShellCompany(
      user?.company_name || companyNameForShell(companies, companyId),
      companyId,
    );
  }, [companies, companyId, user?.company_name]);

  // Release 1.0.2 · Rifattorizzazione · Profilo VIGENTE (non media annuale).
  // La pagina Profili Orari mostra il contratto valido oggi.
  // Endpoint: /current-profile-summary (motore services/profile_current.py).
  const [ptDetails, setPtDetails] = useState(null);
  const [ptLoading, setPtLoading] = useState(false);
  const [ptRefreshTick, setPtRefreshTick] = useState(0);
  useEffect(() => {
    if (!employeeId) { setPtDetails(null); return; }
    let cancelled = false;
    (async () => {
      setPtLoading(true);
      try {
        const { data } = await api.get(`/employees/${employeeId}/current-profile-summary`);
        if (!cancelled) setPtDetails(data);
      } catch (_e) {
        if (!cancelled) setPtDetails(null);
      } finally {
        if (!cancelled) setPtLoading(false);
      }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [employeeId, profiles.length, ptRefreshTick]);

  const fmtIt = (n, d = 2) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    return Number(n).toFixed(d).replace(".", ",");
  };



  const todayIso = new Date().toISOString().slice(0, 10);
  const currentProfile = [...profiles]
    .filter((profile) => profile.effective_from <= todayIso)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] || null;

  const openNew = () => {
    // Un nuovo profilo rappresenta normalmente una variazione dell'orario
    // vigente: eredita quindi anche alternanza e sabati variabili. In questo
    // modo una nuova decorrenza non azzera silenziosamente tali condizioni.
    if (currentProfile) {
      const {
        id, employee_id, created_at, effective_from, name, ...inherited
      } = currentProfile;
      setForm({
        ...empty(),
        ...inherited,
        name: "Nuovo orario",
        effective_from: todayIso,
      });
    } else {
      setForm(empty());
    }
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (p) => { setForm({ ...p }); setEditingId(p.id); setOpen(true); };

  const save = async () => {
    try {
      if (editingId) await api.patch(`/hour-profiles/${editingId}`, form);
      else await api.post("/hour-profiles", { ...form, employee_id: employeeId });
      toast.success("Profilo salvato"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il profilo orario?")) return;
    try { await api.delete(`/hour-profiles/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const totalWeek = (p) => days.reduce((a, d) => a + Number(p[d] || 0), 0);

  return (
    <div className="space-y-6 max-w-[1400px]">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Configurazione</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1 text-slate-900" style={{ fontFamily: "Manrope" }}>Profili orari</h1>
          <p className="text-sm text-slate-500 mt-1">Ore teoriche settimanali per dipendente, storicizzate per data di decorrenza.</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          {["admin", "super_admin"].includes(effectiveRole) && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Azienda</Label>
              <Select
                value={companyId || ""}
                onValueChange={setCompanyId}
              >
                <SelectTrigger className="w-56"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Dipendente</Label>
            <SuiteEmployeePicker employees={employees} value={employeeId} onValueChange={setEmployeeId}
              className="w-72" testId="profiles-employee-select" />
          </div>
          <Button onClick={openNew} disabled={!employeeId} className="bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="new-profile-btn">
            <Plus size={16} className="mr-2" /> Nuovo profilo
          </Button>
        </div>
      </div>

      {employeeId && (
        <Card className="border-emerald-200 rounded-md bg-emerald-50/40" data-testid="current-profile-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">
                  Profilo orario vigente
                </div>
                <div className="text-[11px] text-emerald-900/80 mt-0.5" data-testid="current-profile-effective-from">
                  {ptDetails?.effective_from
                    ? `Decorrenza: ${formatDateIT(ptDetails.effective_from)}`
                    : "Nessun profilo vigente"}
                </div>
                {ptDetails?.validity_label && (
                  <div className="text-[11px] text-emerald-900/70" data-testid="current-profile-validity-label">
                    {ptDetails.validity_label}
                  </div>
                )}
                <div className="text-3xl font-bold text-emerald-900 mt-1" data-testid="current-profile-pct-value">
                  {ptLoading ? "…" : (ptDetails ? `${fmtIt(ptDetails.percentage, 2)} %` : "—")}
                </div>
                {ptDetails?.explanation && (
                  <div className="text-xs text-slate-700 mt-1 max-w-2xl" data-testid="current-profile-explanation">
                    {ptDetails.explanation}
                  </div>
                )}
              </div>
              {ptDetails && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <div className="text-slate-500 uppercase tracking-wide">Ore settimana</div>
                    <div className="font-mono font-semibold text-slate-900" data-testid="current-profile-weekly-hours">
                      {fmtHours(ptDetails.weekly_hours)} h
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500 uppercase tracking-wide">Ore annue</div>
                    <div className="font-mono font-semibold text-slate-900" data-testid="current-profile-annual-hours">
                      {fmtHours(ptDetails.annual_hours)} h
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500 uppercase tracking-wide">Media mensile</div>
                    <div className="font-mono font-semibold text-slate-900">{fmtHours(ptDetails.monthly_average)} h</div>
                  </div>
                  <div>
                    <div className="text-slate-500 uppercase tracking-wide">Media giornaliera</div>
                    <div className="font-mono font-semibold text-slate-900">{fmtHours(ptDetails.daily_average)} h</div>
                  </div>
                </div>
              )}
            </div>
            {ptDetails && (
              <div className="mt-3 text-[11px] text-slate-500">
                Divisore CCNL: <b className="text-slate-700">{fmtIt(ptDetails.annual_divisor, 0)} h</b> ({fmtIt(ptDetails.monthly_divisor, 0)} × 12)
                {ptDetails.divisor_source === "ccnl" && ptDetails.divisor_ccnl_name
                  ? ` · ${ptDetails.divisor_ccnl_name}`
                  : " · fallback 170"}
                {Number(ptDetails.saturday_quota_hours || 0) > 0 && (
                  <> · Quota sabato: <b className="text-slate-700">{fmtHours(ptDetails.saturday_quota_hours)} h/anno</b></>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 rounded-md">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-widest">Nome</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Decorrenza</TableHead>
                {WEEKDAYS_FULL.map((d) => (
                  <TableHead key={d} className="text-xs uppercase tracking-widest text-right">{d.slice(0, 3)}</TableHead>
                ))}
                <TableHead className="text-xs uppercase tracking-widest text-right">Totale/set.</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.map((p) => (
                <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`profile-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <CalendarClock size={14} className="text-emerald-700" />
                      <span className="font-semibold">{p.name}</span>
                      {p.id === currentProfile?.id && (
                        <span
                          className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800"
                          title="Profilo applicato attualmente dalla Gestione Presenze"
                          data-testid={`profile-current-${p.id}`}
                        >
                          Vigente
                        </span>
                      )}
                    </div>
                    {Number(p.saturday_quota_days_per_month || 0) > 0 && (
                      <div className="mt-1 text-[11px] font-medium text-sky-700">
                        {p.saturday_quota_days_per_month} sabati variabili ×{" "}
                        {fmtHours(p.saturday_hours_per_worked_day || 0)} ore
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono">{formatDateIT(p.effective_from)}</TableCell>
                  {days.map((d) => (
                    <TableCell key={d} className="text-right font-mono">{fmtHours(p[d])}</TableCell>
                  ))}
                  <TableCell className="text-right font-mono font-semibold">{fmtHours(totalWeek(p))}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="outline" size="sm" onClick={() => openEdit(p)}
                              data-testid={`profile-edit-${p.id}`}>
                        <Pencil size={14} className="mr-1.5" /> Modifica
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => del(p.id)}
                              className="text-red-700 border-red-200 hover:bg-red-50"
                              data-testid={`profile-delete-${p.id}`}>
                        <Trash2 size={14} className="mr-1.5" /> Elimina
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {profiles.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">Nessun profilo</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Eccezioni al profilo orario per data specifica */}
      {employeeId && (
        <ScheduleExceptionsCard
          employeeId={employeeId}
          employeeName={(employees.find((e) => e.id === employeeId) || {})
            ? `${(employees.find((e) => e.id === employeeId) || {}).last_name || ""} ${(employees.find((e) => e.id === employeeId) || {}).first_name || ""}`.trim()
            : ""}
          onExceptionsChanged={() => setPtRefreshTick((t) => t + 1)}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent formDialog className="max-w-3xl">
          <DialogHeader><DialogTitle>{editingId ? "Modifica profilo" : "Nuovo profilo orario"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Decorrenza dal</Label><Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="profile-form-effective-from" /></div>
            </div>
            <div className="flex justify-end -mb-1"><DurationFormatBadge data-testid="profile-form-format-badge" /></div>
            <div className="grid grid-cols-7 gap-2">
              {days.map((d, di) => (
                <div key={d}>
                  <Label className="text-xs">{WEEKDAYS_FULL[di]}</Label>
                  <DurationInput value={form[d]} onCommit={(n) => setForm((f) => ({ ...f, [d]: n ?? 0 }))} data-testid={`profile-form-${d}`} />
                </div>
              ))}
            </div>

            {/* M-WF-01 · Orari previsti (facoltativi): precompilano "Compila Orari" (modalità B) */}
            <details className="rounded-md border border-suite-neutral-200 p-3" open={!!form.schedule && Object.keys(form.schedule).length > 0} data-testid="profile-form-schedule-section">
              <summary className="cursor-pointer text-sm font-semibold text-suite-neutral-700">Orari previsti (facoltativi)</summary>
              <p className="text-[11px] text-suite-neutral-500 my-2">Entrata/uscita previste per ciascun giorno. Non cambiano le ore teoriche: servono a precompilare &quot;Compila Orari&quot;.</p>
              {["A", ...(form.alternation_active ? ["B"] : [])].map((wk) => (
                <div key={wk} className="mt-2">
                  {form.alternation_active && <div className="text-xs font-semibold mb-1">Settimana {wk}</div>}
                  <WeeklyScheduleEditor value={form.schedule || {}} week={wk} testid="profile-schedule"
                    dayHours={Object.fromEntries(days.map((d, i) => [i, form[wk === "B" ? `${d}_alt` : d]]))}
                    onChange={(schedule) => setForm((f) => ({ ...f, schedule }))}
                    onProposeHours={(w, wd, h) => setForm((f) => ({ ...f, [w === "B" ? `${days[wd]}_alt` : days[wd]]: Math.round(h * 1e6) / 1e6 }))} />
                </div>
              ))}
            </details>

            {/* --- Alternanza settimanale A/B --- */}
            <div className="border-t border-slate-200 pt-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" checked={!!form.alternation_active}
                       onChange={(e) => {
                         const enabled = e.target.checked;
                         setForm((current) => {
                           if (!enabled || current.alternation_active) {
                             return { ...current, alternation_active: enabled };
                           }
                           // Quando si attiva l'alternanza, la settimana B
                           // nasce uguale alla A. L'utente modifica soltanto
                           // i giorni realmente diversi, evitando zeri
                           // involontari sugli altri giorni.
                           const inheritedAlt = {};
                           days.forEach((day) => {
                             inheritedAlt[`${day}_alt`] = Number(current[day] || 0);
                           });
                           return {
                             ...current,
                             ...inheritedAlt,
                             alternation_active: true,
                           };
                         });
                       }}
                       data-testid="alternation-active-checkbox" />
                Attiva alternanza settimanale A/B
                <span className="text-[11px] text-slate-500 font-normal">(es. Marri Elisa: mercoledì 6,25 vs 8,25 alternati)</span>
              </label>
              {form.alternation_active && (
                <div className="mt-3 space-y-3 bg-amber-50/40 border border-amber-200 rounded-md p-3">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold">Riferimento:</span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="alt-week"
                             checked={form.alternation_starts_on_odd_week === true}
                             onChange={() => setForm({ ...form, alternation_starts_on_odd_week: true })} />
                      Settimane <b>dispari</b> = A (righe base)
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="alt-week"
                             checked={form.alternation_starts_on_odd_week === false}
                             onChange={() => setForm({ ...form, alternation_starts_on_odd_week: false })} />
                      Settimane <b>pari</b> = A
                    </label>
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {days.map((d, di) => (
                      <div key={`alt-${d}`}>
                        <Label className="text-xs">{WEEKDAYS_FULL[di]} <span className="text-amber-700">(sett. B)</span></Label>
                        <DurationInput value={form[`${d}_alt`] || 0}
                               onCommit={(n) => setForm((f) => ({ ...f, [`${d}_alt`]: n ?? 0 }))}
                               data-testid={`profile-form-${d}-alt`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* --- Monte-ore sabati mensile --- */}
            <div className="border-t border-slate-200 pt-3">
              <div className="text-sm font-semibold text-slate-700 mb-1">Monte-ore SABATO mensile (ordinario)</div>
              <div className="text-[11px] text-slate-500 mb-2">
                Es. Marri: 2 sabati/mese × 5h. I primi N sabati lavorati del mese entrano nel monte-ore ordinario retribuito.
                Le date specifiche si compilano nel cartellino: <b>il programma le riconosce in automatico</b>.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>N. sabati ordinari al mese</Label>
                  <Input type="number" min="0" max="5" step="1"
                         value={form.saturday_quota_days_per_month || 0}
                         onChange={(e) => setForm({ ...form, saturday_quota_days_per_month: Number(e.target.value) })}
                         data-testid="profile-form-sat-quota-days" />
                </div>
                <div>
                  <DurationLabel data-testid="profile-form-sat-quota-hours">Ore per ciascun sabato</DurationLabel>
                  <DurationInput
                         value={form.saturday_hours_per_worked_day || 0}
                         onCommit={(n) => setForm((f) => ({ ...f, saturday_hours_per_worked_day: n ?? 0 }))}
                         data-testid="profile-form-sat-quota-hours" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="save-profile-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
